import React, {
	useRef,
	useContext,
	useEffect,
	useState,
	useCallback,
} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Icon from 'tbk-components/src/components/Icon';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import ResourcesFilterBar from '../ResourcesFilterBar';
import useHashAsState from 'tbk-components/src/hooks/useHashAsState';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Dropdown from 'bootstrap/js/dist/dropdown';
import { debounce, has, set, update } from 'lodash';
import Loading from 'tbk-components/src/components/Loading';
import ElasticMapper from 'tbk-components/src/services/ElasticMapper';
import Button from 'tbk-components/src/components/Button';

export interface ISearchBannerI18nProps {
	searchPlaceholder?: string;
	viewAdvancedSearch?: string;
	noResults?: string;
	threeCharacterMinimum?: string;
}

export interface ISearchBannerProps extends IBaseElementProps {
	title?: string;
	/**
	 * @control richtext
	 */
	excerpt?: string;
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	assets?: any;
	/**
	 * @noUI
	 */
	posts?: {
		total: number;
		hits: any[];
	};
	fetchArgs?: any;
	searchResults?: any;
	/**
	 * @noUI
	 */
	advancedSearchUrl?: string;
	searchPlaceholder?: string;
	onCancel?: () => void;
	inverted?: boolean;
	i18n: ISearchBannerI18nProps;
}

/**
 * Calian Search Banner
 * @icon search
 * @block
 */
const SearchBanner: React.FC<ISearchBannerProps> = withAppContext(
	({
		className,
		classNames = [],
		context,
		title,
		posts,
		excerpt,
		fetchArgs,
		advancedSearchUrl,
		searchPlaceholder,
		inverted = false,
		i18n,
	}) => {
		const { t } = useTranslation();
		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);
		const [assets, setAssets] = useState<any>(null);
		const [dropdownVisible, setDropdownVisible] = useState(false);
		const [searchResults, setSearchResults] = useState<any>();
		const [commonTermAssets, setCommonTermAssets] = useState<any>();
		const topRef = useRef(null);
		const [loading, setLoading] = useState<boolean>(false);
		const [hasSearched, setHasSearched] = useState(false);
		const handleDropdownClose = () => {
			setDropdownVisible(false);
		};

		const [advancedUrlWithQuery, setAdvancedUrlWithQuery] =
			useState<string>(advancedSearchUrl || '');
		const inputRef = useRef<HTMLInputElement>(null);
		const dropdownRef = useRef<HTMLDivElement>(null);
		const formRef = useRef<HTMLFormElement>(null);

		const taxonomy_parameters = ((taxonomies) => {
			const t: any = {};
			(taxonomies || []).forEach((tax: any) => {
				t[tax.taxonomy] = 'csv';
			});
			return t;
		})(assets?.taxonomies);
		const [filters, setFilters] = useState({
			page: 'int',
			...taxonomy_parameters,
			search: 'string',
			total: 'int',
		});
		const [total, setTotal] = useState<number | null>(null);

		useEffect(() => {
			if (dropdownVisible) {
				const handleClickOutside = (event: MouseEvent) => {
					if (
						dropdownRef.current &&
						!dropdownRef.current.contains(event.target as Node) &&
						inputRef.current &&
						!inputRef.current.contains(event.target as Node)
					) {
						handleDropdownClose();
					}
				};

				const handleEscKey = (event: KeyboardEvent) => {
					if (event.key === 'Escape') {
						handleDropdownClose();
					}
				};

				const handleLastDropdownItem = (event: KeyboardEvent) => {
					handleDropdownClose();
				};

				document.addEventListener('keydown', handleEscKey);
				document.addEventListener('click', handleClickOutside);

				return () => {
					document.removeEventListener('click', handleClickOutside);
					document.removeEventListener('keydown', handleEscKey);
				};
			}
		}, [dropdownVisible, handleDropdownClose]);

		const fetchPostsFromApi = useCallback(
			debounce((args: any) => {
				if (args.search) {
					setAdvancedUrlWithQuery(
						`${advancedSearchUrl}#!search=${args.search}`,
					);
				}

				const getBlogRoll = Api['getBlogRollSearchWP'];
				getBlogRoll(args)
					.then((results: any) => {
						setTotal(results.total);
						if (results.hits) {
							setSearchResults(ElasticMapper(results));
						}
					})
					.finally(() => {
						setLoading(false);
					});
			}, 100),
			['getBlogRollSearchWP'],
		);
		useEffect(() => {
			const assetReqArgs = { ...fetchArgs };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
				const map: any = {};
				if (results.taxonomies) {
					results.taxonomies.forEach((taxonomy: any) => {
						taxonomy.terms.forEach((term: any) => {
							map[term.term_id] = term;
						});
					});
				}
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			}

			if (!topRef.current) {
				return;
			}
		}, [contextAssets, Api, fetchArgs]);

		const onSearchChange = (e) => {
			setFilters({
				page: 1,
				...filters,
				search: e.target.value,
			});
			setSearchResults(null);
		};

		const searchArgs = qs.stringify(filters);

		useEffect(() => {
			if (posts) {
				setTotal(posts.total);
				setSearchResults(ElasticMapper(posts));
				return;
			}
			if (!assets) {
				return;
			}
			// Compile our Api call args from the hash query.
			let args = qs.parse(searchArgs.replace(/^#!/, ''), {
				ignoreQueryPrefix: true,
			});
			args.posts_per_page = '5';

			if (fetchArgs) {
				args = {
					...fetchArgs,
					...args,
				};
			}

			fetchPostsFromApi(args);
			setSearchResults(null);
			setHasSearched(true);
			setLoading(true);
		}, [filters]);

		useEffect(() => {
			const assetReqArgs = { ...fetchArgs };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			} else {
				Api.getBlogRollAssets(assetReqArgs).then((results: any) => {
					setAssetsAndTermMap(results);
				});

				setAssetsAndTermMap(posts);
			}

			if (!topRef.current) {
				return;
			}

			const initDropdown = () => {
				const dropdownTriggerList = [].slice.call(
					topRef.current.querySelectorAll('.dropdown'),
				);

				dropdownTriggerList.map(
					(dropdownTriggerEl) => new Dropdown(dropdownTriggerEl),
				);
			};

			initDropdown();
		}, [contextAssets, Api, fetchArgs]);

		return (
			<div
				ref={topRef}
				className={cn(
					className || [
						'calian-search-banner',
						'bg-gradient-color-3',
						'py-lg-7 py-5',
						'px-3',
						'position-relative',
						'z-2',
						...classNames,
						inverted && 'inverted',
					],
					!loading && hasSearched && 'has-content',
				)}
			>
				<div className="calian-search-banner__content mw-604px mx-auto position-relative">
					{(title || excerpt) && (
						<div className="calian-search-banner__copy text-center mb-3 d-inline-flex flex-column gap-2">
							{title && (
								<h2 className="mb-0 h4 text-white">{title}</h2>
							)}
							{excerpt && (
								<p
									className="mb-0 text-white text-body-sm"
									dangerouslySetInnerHTML={{
										__html: excerpt,
									}}
								/>
							)}
						</div>
					)}
					<div
						className="z-1 position-relative"
						ref={inputRef}
						onFocus={() => setDropdownVisible(true)}
					>
						<ResourcesFilterBar
							searchPlaceholder={
								i18n?.searchPlaceholder
									? i18n?.searchPlaceholder
									: t('Search all Calian websites')
							}
							searchTaxonomies={assets}
							assets={assets}
							onSearchChange={onSearchChange}
							listStyle={'default'}
							showFilterBar={false}
							filterHeaderBackgroundStyle={'light'}
							showSearchResultsCount={false}
							goToAdvancedSearch
							searchAction={advancedSearchUrl}
						/>
					</div>
					{dropdownVisible && hasSearched && (
						<div
							ref={dropdownRef}
							className="calian-search-banner__results-container w-100 z-0 bg-white position-absolute z-0"
						>
							{loading ? (
								<div className="calian-search-banner__results-loading py-md-8 py-4">
									<Loading />
								</div>
							) : (
								<div
									className={cn(
										'calian-search-banner__results py-2 px-3',
									)}
								>
									{searchResults?.length > 0 ? (
										<div className="">
											{searchResults?.length > 0 && (
												<ul className="results-listing list-unstyled">
													{searchResults.map(
														(result, index) => {
															return (
																<li
																	key={index}
																	className="result-item"
																>
																	{result?.cta
																		?.href && (
																		<a
																			href={
																				result
																					?.cta
																					?.href
																			}
																			className={cn(
																				index !==
																					0 &&
																					'border-top',
																				index ===
																					searchResults.length -
																						1 &&
																					'border-bottom',
																				'text-decoration-none text-charcoal px-3 py-2 d-flex justify-content-between gap-3',
																			)}
																		>
																			{result?.heading && (
																				<div
																					className="mb-0"
																					dangerouslySetInnerHTML={{
																						__html: result?.heading,
																					}}
																				/>
																			)}
																			<Icon name="arrow-right" />
																		</a>
																	)}
																</li>
															);
														},
													)}
												</ul>
											)}
										</div>
									) : (
										<div className="no-results py-5 text-center">
											<p className="py-4">
												{filters.search.length < 3
													? i18n?.threeCharacterMinimum ||
													  t(
															'Please enter at least 3 characters',
													  )
													: i18n?.noResults ||
													  t('No results found')}
											</p>
										</div>
									)}

									{advancedUrlWithQuery && (
										<div className="view-advanced-search-banner-container">
											<Button
												className="text-decoration-none d-inline-flex gap-3 align-items-center flex-row-reverse px-3 mb-3"
												label={
													i18n?.viewAdvancedSearch ||
													t('View Advanced Search')
												}
												href={advancedUrlWithQuery}
												icon="arrow-right"
											/>
										</div>
									)}
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		);
	},
);
export default SearchBanner;
